/**
 * nh-delivery-bar.js
 *
 * Delivery and stock bar for Nouvel Heritage PDPs.
 *
 * Form submission: native Shopify contact form (same pattern as contact-hub).
 * The {% form 'contact' %} Liquid tag inside the block renders a real form with
 * authenticity_token. JS intercepts submit, validates, populates hidden fields,
 * sets return_to = current URL + ?waitlist=true&size=XX, then calls form.submit().
 * On page reload, #init() reads the URL params and shows the success state.
 */

// ── Business day utilities ─────────────────────────────────────────────────

function easterSunday(y) {
  var a = y % 19, b = Math.floor(y / 100), c = y % 100;
  var d = Math.floor(b / 4), e = b % 4;
  var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  var h = (19 * a + b - d - g + 15) % 30;
  var i = Math.floor(c / 4), k = c % 4;
  var l = (32 + 2 * e + 2 * i - h - k) % 7;
  var m = Math.floor((a + 11 * h + 22 * l) / 451);
  var month = Math.floor((h + l - 7 * m + 114) / 31);
  var day   = ((h + l - 7 * m + 114) % 31) + 1;
  return [month, day];
}

function nthWeekday(year, month, weekday, n) {
  if (n > 0) {
    var first = new Date(year, month - 1, 1);
    return 1 + ((weekday - first.getDay() + 7) % 7) + (n - 1) * 7;
  }
  var last = new Date(year, month, 0);
  return last.getDate() - ((last.getDay() - weekday + 7) % 7);
}

function isHoliday(d) {
  var m = d.getMonth() + 1, day = d.getDate(), y = d.getFullYear();
  if (m === 1  && day === 1)  return true;
  if (m === 7  && day === 4)  return true;
  if (m === 7  && day === 14) return true;
  if (m === 12 && day === 25) return true;
  var easter = easterSunday(y);
  var eMon = new Date(y, easter[0] - 1, easter[1] + 1);
  if (d.getFullYear() === eMon.getFullYear() && d.getMonth() === eMon.getMonth() && d.getDate() === eMon.getDate()) return true;
  if (m === 5  && day === nthWeekday(y, 5,  1, -1)) return true;
  if (m === 9  && day === nthWeekday(y, 9,  1,  1)) return true;
  if (m === 11 && day === nthWeekday(y, 11, 4,  4)) return true;
  return false;
}

function getDeliveryDate(n) {
  var d = new Date(), count = 0;
  while (count < n) {
    d.setDate(d.getDate() + 1);
    var dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    var check = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (isHoliday(check)) continue;
    count++;
  }
  var DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
  return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate();
}

// ── Clean size label from variant title ────────────────────────────────────
// e.g. "49 (FR) | 4.75 (US)" → "49"  |  "15cm - XS" → "XS"

function extractSizeLabel(variantTitle) {
  var t = variantTitle || '';
  var sep = t.includes(' | ') ? ' | ' : t.includes(' - ') ? ' - ' : t.includes(' / ') ? ' / ' : null;
  if (!sep) return t;
  var parts = t.split(sep);
  var pa = parts[0].trim(), pb = (parts[1] || '').trim();
  if (/\(FR\)/i.test(pa))  return pa.replace(/\s*\(FR\)/i, '').trim();
  if (/\(FR\)/i.test(pb))  return pb.replace(/\s*\(FR\)/i, '').trim();
  if (/cm/i.test(pa))       return pb;
  return pa;
}

// ── Custom element ─────────────────────────────────────────────────────────

class NHDeliveryBar extends HTMLElement {
  #inventory     = {};
  #country       = 'us';
  #region        = 'north_america';
  #variantId     = null;
  #variantTitle  = '';
  #productTitle  = '';
  #isOneSize     = false;
  #nyDays        = 2;
  #parisDays     = 4;
  #parisToNyDays = 14;
  #intlDays      = 4;

  connectedCallback() {
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    // Parse inventory JSON embedded by Liquid
    var script = this.querySelector('script[type="application/json"]');
    if (script) {
      try { this.#inventory = JSON.parse(script.textContent).variants || {}; } catch (_) {}
    }

    this.#country       = (this.dataset.country       || 'us').toLowerCase();
    this.#variantId     = this.dataset.variantId       || null;
    this.#variantTitle  = this.dataset.variantTitle    || '';
    this.#productTitle  = this.dataset.productTitle    || '';
    this.#isOneSize     = this.dataset.oneSize === 'true';
    this.#nyDays        = parseInt(this.dataset.nyDays,        10) || 2;
    this.#parisDays     = parseInt(this.dataset.parisDays,     10) || 4;
    this.#parisToNyDays = parseInt(this.dataset.parisToNyDays, 10) || 14;
    this.#intlDays      = parseInt(this.dataset.intlDays,      10) || 4;

    this.#region = (this.#country === 'us' || this.#country === 'ca')
      ? 'north_america'
      : this.#country === 'fr' ? 'france' : 'international';

    // Listen for variant changes
    var section = this.closest('.shopify-section');
    if (section) section.addEventListener('variant:update', this.#onVariantUpdate.bind(this));

    // ── Check for waitlist success redirect (same as contact hub pattern) ──
    var params = new URLSearchParams(window.location.search);
    if (params.get('waitlist') === 'true') {
      var sizeParam = decodeURIComponent(params.get('size') || '');
      var msg;
      if (this.#isOneSize) {
        msg = "You\u2019re on the list. We\u2019ll let you know when it\u2019s back.";
      } else if (sizeParam) {
        msg = "You\u2019re on the list. We\u2019ll let you know when size " + sizeParam + " is back.";
      } else {
        msg = "You\u2019re on the list. We\u2019ll be in touch when it\u2019s available.";
      }
      window.history.replaceState({}, '', window.location.pathname);
      // Show bar in red state with success message
      var self = this;
      requestAnimationFrame(function() {
        self.hidden = false;
        self.style.opacity = '1';
        self.classList.add('is-red');
        var hl = self.querySelector('[data-nhsg-delivery-headline]');
        if (hl) hl.textContent = self.#isOneSize ? 'Currently unavailable' : 'Currently unavailable in this size';
        var deliveryForm = self.querySelector('[data-nhsg-delivery-form]');
        if (deliveryForm) deliveryForm.classList.add('is-submitted');
        var success = self.querySelector('[data-nhsg-notify-success]');
        if (success) { success.textContent = msg; success.hidden = false; }
      });
      return; // Don't render the normal delivery state on this load
    }

    // ── Wire native form submit ──
    var nativeForm = this.querySelector('[data-nhsg-native-form]');
    if (nativeForm) {
      nativeForm.addEventListener('submit', this.#handleSubmit.bind(this));
    }

    // Render initial delivery state
    if (this.#variantId) this.#renderForVariant(this.#variantId);
  }

  // ── Variant change from theme ──────────────────────────────────────────────

  #onVariantUpdate(e) {
    var variant = e.detail && e.detail.resource;
    if (!variant || !variant.id) return;
    this.#variantId   = String(variant.id);
    this.#variantTitle = variant.title || '';
    if (this.#inventory[this.#variantId]) {
      this.#inventory[this.#variantId].overall = variant.available !== false;
    } else {
      this.#inventory[this.#variantId] = { ny: false, paris: variant.available !== false, overall: variant.available !== false, metafieldsSet: false };
    }
    this.#renderForVariant(this.#variantId);
  }

  // ── Delivery logic ─────────────────────────────────────────────────────────

  #renderForVariant(variantId) {
    var data = this.#inventory[variantId];

    // Safety gate: variant.available is always current
    var unavailableText = this.#isOneSize ? 'Currently unavailable' : 'Currently unavailable in this size';
    if (data && data.overall === false) {
      this.#applyState('red', unavailableText, '');
      return;
    }

    var nyIn, parisIn;
    if (data) {
      if (data.metafieldsSet) {
        nyIn    = data.ny    === true;
        parisIn = data.paris === true;
      } else {
        nyIn    = false;
        parisIn = data.overall === true;
      }
    } else {
      nyIn = false; parisIn = true;
    }

    var state, headline, sub;

    if (this.#region === 'north_america') {
      if (nyIn) {
        state    = 'green';
        headline = 'Available \u00b7 Delivery by ' + getDeliveryDate(this.#nyDays);
        sub      = 'Complimentary express delivery';
      } else if (parisIn) {
        state    = 'amber';
        headline = 'Available \u00b7 Delivery by ' + getDeliveryDate(this.#parisToNyDays);
        sub      = 'Complimentary express delivery';
      } else {
        state    = 'red';
        headline = unavailableText;
        sub      = '';
      }
    } else if (this.#region === 'france') {
      if (parisIn) {
        state    = 'green';
        headline = 'Available \u00b7 Delivery by ' + getDeliveryDate(this.#parisDays);
        sub      = 'Complimentary express delivery';
      } else {
        state    = 'red';
        headline = unavailableText;
        sub      = '';
      }
    } else {
      if (parisIn) {
        state    = 'amber';
        headline = 'Available \u00b7 Ships from our Paris atelier';
        sub      = 'Complimentary delivery by ' + getDeliveryDate(this.#intlDays);
      } else {
        state    = 'red';
        headline = unavailableText;
        sub      = '';
      }
    }

    this.#applyState(state, headline, sub);
  }

  // ── DOM state update ───────────────────────────────────────────────────────

  /**
   * Writes the delivery estimate as a line item property to the product form.
   * Uses name="properties[_delivery_estimate]" — the _ prefix hides it from
   * Shopify's default checkout and order email UI. We render it explicitly only
   * in our cart snippet and order notification template.
   * @param {string} value
   */
  #writeDeliveryProperty(value) {
    var productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;
    var prop = productForm.querySelector('input[name="properties[_delivery_estimate]"]');
    if (!prop) {
      prop = document.createElement('input');
      prop.type  = 'hidden';
      prop.name  = 'properties[_delivery_estimate]';
      productForm.appendChild(prop);
    }
    prop.value = value;
  }

  #applyState(state, headline, sub) {
    var self = this;
    var wasHidden = this.hidden;

    // Build the delivery estimate string for the line item property
    var estimate = '';
    if (state === 'green' || state === 'amber') {
      if (headline.includes('\u00b7')) {
        var parts = headline.split('\u00b7');
        var datePart = parts[parts.length - 1].trim();
        // International: date is in sub ("Complimentary delivery by April 6")
        estimate = (datePart.toLowerCase().indexOf('paris') !== -1 && sub)
          ? sub
          : datePart;
      }
    }
    this.#writeDeliveryProperty(estimate);

    var update = function() {
      self.classList.remove('is-green', 'is-amber', 'is-red');
      self.classList.add('is-' + state);
      var hl = self.querySelector('[data-nhsg-delivery-headline]');
      var sl = self.querySelector('[data-nhsg-delivery-sub]');
      if (hl) hl.textContent = headline;
      if (sl) sl.textContent = sub;
      if (state !== 'red') self.#resetForm();
      self.hidden = false;
      self.style.opacity = '1';
    };
    if (!wasHidden) {
      this.style.opacity = '0';
      setTimeout(update, 200);
    } else {
      update();
    }
  }

  #resetForm() {
    var form    = this.querySelector('[data-nhsg-delivery-form]');
    var success = this.querySelector('[data-nhsg-notify-success]');
    var error   = this.querySelector('[data-nhsg-notify-error]');
    var consent = this.querySelector('[data-nhsg-notify-consent]');
    if (form)    form.classList.remove('is-submitted', 'has-error');
    if (success) { success.hidden = true; success.textContent = ''; }
    if (error)   { error.hidden = true; error.textContent = ''; }
    if (consent) consent.checked = true;
    this.querySelectorAll('.nhsg-delivery__input').forEach(function(el) {
      el.value = '';
      el.setAttribute('aria-invalid', 'false');
    });
    this.querySelectorAll('.nhsg-delivery__field-error').forEach(function(el) {
      el.textContent = '';
      el.classList.remove('is-visible');
    });
  }

  // ── Validation helpers ─────────────────────────────────────────────────────

  #clearErr(input, errEl) {
    if (input)  input.setAttribute('aria-invalid', 'false');
    if (errEl)  { errEl.textContent = ''; errEl.classList.remove('is-visible'); }
  }

  #setErr(input, errEl, msg, isFirst) {
    if (input)  input.setAttribute('aria-invalid', 'true');
    if (errEl)  { errEl.textContent = msg; errEl.classList.add('is-visible'); }
    if (isFirst && input) input.focus();
  }

  // ── Native form submit ─────────────────────────────────────────────────────

  #handleSubmit(e) {
    e.preventDefault();

    var bid      = this.dataset.bid || '';
    var input    = this.querySelector('[data-nhsg-notify-input]');
    var fname    = this.querySelector('[data-nhsg-notify-fname]');
    var lname    = this.querySelector('[data-nhsg-notify-lname]');
    var city     = this.querySelector('[data-nhsg-notify-city]');
    var country  = this.querySelector('[data-nhsg-notify-country]');
    var consent  = this.querySelector('[data-nhsg-notify-consent]');
    var form     = this.querySelector('[data-nhsg-delivery-form]');
    var error    = this.querySelector('[data-nhsg-notify-error]');

    var fnameErr   = this.querySelector('#nhsg-fname-err-'   + bid);
    var lnameErr   = this.querySelector('#nhsg-lname-err-'   + bid);
    var emailErr   = this.querySelector('#nhsg-email-err-'   + bid);
    var cityErr    = this.querySelector('#nhsg-city-err-'    + bid);
    var countryErr = this.querySelector('#nhsg-country-err-' + bid);
    var consentErr = this.querySelector('#nhsg-consent-err-' + bid);

    // Clear all errors
    [
      [fname,   fnameErr],
      [lname,   lnameErr],
      [input,   emailErr],
      [city,    cityErr],
      [country, countryErr],
    ].forEach(function(pair) { this.#clearErr(pair[0], pair[1]); }, this);
    if (error && form) { error.hidden = true; form.classList.remove('has-error'); }

    // Validate
    var hasError = false;
    var checks = [
      { el: fname,   errEl: fnameErr,   msg: 'Please enter your first name.' },
      { el: lname,   errEl: lnameErr,   msg: 'Please enter your last name.'  },
      { el: city,    errEl: cityErr,    msg: 'Please enter your city.'       },
      { el: country, errEl: countryErr, msg: 'Please enter your country.'    },
    ];
    for (var i = 0; i < checks.length; i++) {
      var c = checks[i];
      if (c.el && !c.el.value.trim()) {
        this.#setErr(c.el, c.errEl, c.msg, !hasError);
        hasError = true;
      }
    }
    var emailVal = input ? input.value.trim() : '';
    if (!emailVal || !emailVal.includes('@')) {
      var eMsg = emailVal ? 'Please enter a valid email address.' : 'Please enter your email address.';
      this.#setErr(input, emailErr, eMsg, !hasError);
      hasError = true;
    }
    if (consent && !consent.checked) {
      if (consentErr) { consentErr.textContent = 'Please agree to the privacy policy.'; consentErr.classList.add('is-visible'); }
      if (!hasError) consent.focus();
      hasError = true;
    }
    if (hasError) return;

    // Build reference number
    var now = new Date();
    var ref = 'NH-'
      + String(now.getFullYear()).slice(2)
      + String(now.getMonth() + 1).padStart(2, '0')
      + String(now.getDate()).padStart(2, '0')
      + '-' + Math.random().toString(36).toUpperCase().slice(2, 6);

    var sizeLabel = extractSizeLabel(this.#variantTitle);
    var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var dateStr = MONTHS[now.getMonth()] + ' ' + now.getDate() + ', ' + now.getFullYear();

    var subject, returnUrl, sizeValue;
    if (this.#isOneSize) {
      subject   = '[Waitlist] ' + this.#productTitle + ' \u00b7 ' + dateStr;
      returnUrl = window.location.pathname + '?waitlist=true';
      sizeValue = 'One size';
    } else {
      subject   = '[Waitlist] ' + this.#productTitle + ' \u2014 Size ' + this.#variantTitle + ' \u00b7 ' + dateStr;
      returnUrl = window.location.pathname + '?waitlist=true&size=' + encodeURIComponent(sizeLabel);
      sizeValue = this.#variantTitle;
    }

    // Populate hidden fields and set return_to, then submit natively
    var nativeForm = this.querySelector('[data-nhsg-native-form]');
    if (!nativeForm) return;

    var set = function(sel, val) {
      var el = nativeForm.querySelector(sel);
      if (el) el.value = val;
    };
    set('[data-nhsg-return-to]', returnUrl);
    set('[data-nhsg-h-subject]', subject);
    set('[data-nhsg-h-ref]',     ref);
    set('[data-nhsg-h-size]',    sizeValue);

    nativeForm.submit();
  }
}

if (!customElements.get('nh-delivery-bar')) {
  customElements.define('nh-delivery-bar', NHDeliveryBar);
}